import React, { useState } from "react";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { CreditCard, Printer, Download, Filter } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { ReportActionToolbar } from "../../components/reports/ReportActionToolbar";
import { PaginationFooter } from "../../components/sunbeds/PaginationFooter";
import {
  PaymentsTable,
  PaymentItem,
} from "../../components/reports/PaymentsTable";
import { ReportDateRangePicker } from "../../components/reports/ReportDateRangePicker";
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  format,
} from "date-fns";
import { useAuth } from "../../auth/AuthContext";

const generateMockPaymentsData = (currencySymbol: string): PaymentItem[] => {
  const customers = ["Jane Doe", "John Smith", "Emma Wilson", "Liam Brown", "Walk-in Guest", "Sarah Conner", "Mike Ross"];
  const methods: ("Cash" | "Card" | "Gift Card" | "Online" | "Voucher")[] = ["Cash", "Card", "Card", "Card", "Online", "Gift Card"];
  const types: ("Payment" | "Refund" | "Deposit")[] = ["Payment", "Payment", "Payment", "Payment", "Refund", "Deposit"];
  const statuses: ("Success" | "Failed" | "Voided")[] = ["Success", "Success", "Success", "Success", "Voided", "Failed"];

  return Array.from({ length: 50 }).map((_, i) => {
    const isToday = i < 8;
    const date = subDays(new Date(), isToday ? 0 : Math.floor(i / 6));
    date.setHours(10 + (i % 8), Math.floor(Math.random() * 60));

    const amountBase = Math.floor(Math.random() * 120) + 10;
    const type = types[Math.floor(Math.random() * types.length)];
    let status = statuses[Math.floor(Math.random() * statuses.length)];
    if (type === "Payment") status = "Success";

    return {
      id: `pay-${i}`,
      paymentRef: `PAY-${20250000 + i}`,
      orderRef: `ORD-${20250000 + i}`,
      date: format(date, "dd MMM yyyy, hh:mm a"),
      dateObj: date,
      customerName: customers[Math.floor(Math.random() * customers.length)],
      method: methods[Math.floor(Math.random() * methods.length)],
      type: type,
      amount: `${currencySymbol}${amountBase.toFixed(2)}`,
      status: status,
    };
  });
};

export default function PaymentsPage() {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  const [payments] = useState<any[]>(() => generateMockPaymentsData(currencySymbol));
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [isLoading, setIsLoading] = useState(false);

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: startOfMonth(new Date()),
    end: new Date(),
  });

  const filteredPayments = payments.filter((item) => {
    const matchesSearch =
      item.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.paymentRef.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.orderRef.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesDate = true;
    if (dateRange.start && dateRange.end && item.dateObj) {
      matchesDate =
        item.dateObj >= startOfDay(dateRange.start) &&
        item.dateObj <= endOfDay(dateRange.end);
    }

    return matchesSearch && matchesDate;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="flex-1 flex flex-col space-y-6 overflow-hidden p-4">
      <SubPageHeader
        title="Payments Report"
        subtitle="Track all incoming payments, refunds, and deposits"
        icon={CreditCard}
        actions={
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 lg:gap-3 w-full sm:w-auto">
            <div className="flex-1 sm:flex-none min-w-[140px]">
              <ReportDateRangePicker
                startDate={dateRange.start}
                endDate={dateRange.end}
                onChange={(start, end) => setDateRange({ start, end })}
              />
            </div>
            <Button
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center justify-center gap-2 h-10 px-4 sm:px-3 shadow-sm rounded-xl"
              title="Print Report"
            >
              <Printer size={18} />
              <span className="sm:hidden font-bold">Print</span>
            </Button>
          </div>
        }
      />

      <ReportActionToolbar
        totalItems={payments.length}
        showingStart={filteredPayments.length > 0 ? indexOfFirstItem + 1 : 0}
        showingEnd={Math.min(indexOfLastItem, filteredPayments.length)}
        searchTerm={searchTerm}
        onSearchChange={(val) => {
          setSearchTerm(val);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search payment ref, order ref, or customer..."
      >
        <Button
          variant="outline"
          className="h-[38px] w-[38px] p-0 flex items-center justify-center border-gray-200 text-gray-500 hover:text-[#885ABB] hover:border-[#885ABB] bg-white"
          title="Export CSV"
        >
          <Download size={18} />
        </Button>
      </ReportActionToolbar>

      <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
        <PaymentsTable
          payments={currentPayments}
          isLoading={isLoading}
          indexOfFirstItem={indexOfFirstItem}
        />
        <PaginationFooter
          totalItems={payments.length}
          filteredTotal={filteredPayments.length}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
}
